import React, { useState, useEffect, useContext } from "react";
import SoftwareFinderDialog from "./SoftwareFinderDialog";
import EmployeeFinderDialog from "./EmployeeFinderDialog";
import PersonFinderDialog from "./PersonFinderDialog";
import SearchAgentsDialog from "./SearchAgentsDialog";
import { AuthContext } from "../context/AuthContext";
import { AgentService } from "../services/AgentService";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Bot, PlusIcon, Search, Users2, UserSearch } from "lucide-react";

const OtherAIDialog = ({ isOpen, onClose }) => {
  const [currentTool, setCurrentTool] = useState(null);
  const [savedAgents, setSavedAgents] = useState([]);
  const [isSearchAgentsOpen, setIsSearchAgentsOpen] = useState(false);
  const { currentUser } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(false);

  const builtInTools = [
    // {
    //   name: "Software Finder",
    //   description:
    //     "Discover impactful & innovative software solutions tailored to your needs",
    //   icon: <Search className="w-8 h-8" />,
    //   component: SoftwareFinderDialog,
    // },
    // {
    //   name: "Employee Finder",
    //   description:
    //     "Find the right talent and team members for your organization",
    //   icon: <Users2 className="w-8 h-8" />,
    //   component: EmployeeFinderDialog,
    // },
    {
      name: "Professional Search",
      description: "Research and connect with professionals in your industry",
      icon: <UserSearch className="w-8 h-8" />,
      component: PersonFinderDialog,
    },
  ];

  // Create an array of all tools including built-in and saved agents
  const allTools = [
    ...builtInTools,
    ...savedAgents.map(agent => ({
      name: agent.name,
      description: agent.description,
      icon: <img src={agent.icon.startsWith("/") ? `https://agent.ai${agent.icon}` : agent.icon} alt={agent.name} className="w-8 h-8" />,
      component: (props) => (
        <SearchAgentsDialog
          {...props}
          initialAgent={agent}
          onAgentSaved={(savedAgent) => {
            // Agent already saved
          }}
          onAgentRemoved={(agentId) => {
            setSavedAgents(prev => prev.filter(a => a.id !== agentId));
          }}
        />
      ),
      isAgent: true,
      agentData: agent
    }))
  ];

  useEffect(() => {
    const loadSavedAgents = async () => {
      if (currentUser?.uid && isOpen) {
        setIsLoading(true);
        try {
          const agents = await AgentService.getUserAgents(currentUser.uid);
          setSavedAgents(agents);
        } catch (error) {
          console.error("Error loading saved agents:", error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    loadSavedAgents();
  }, [currentUser?.uid, isOpen]);

  const handleToolSelect = (tool) => {
    setCurrentTool(tool);
  };

  const handleClose = () => {
    setCurrentTool(null);
    setIsSearchAgentsOpen(false);
    onClose();
  };

  const handleRemoveAgent = async (e, agentId) => {
    e.stopPropagation();
    if (window.confirm("Are you sure you want to remove this agent?")) {
      try {
        await AgentService.removeAgent(agentId);
        setSavedAgents(prev => prev.filter(agent => agent.id !== agentId));
      } catch (error) {
        console.error("Error removing agent:", error);
      }
    }
  };

  if (!isOpen) return null;

  if (currentTool) {
    const ToolComponent = currentTool.component;
    return <ToolComponent
      isOpen={true}
      onClose={() => {
        setCurrentTool(null);
      }}
    />;
  }

  if (isSearchAgentsOpen) {
    return (
      <SearchAgentsDialog
        isOpen={true}
        onClose={() => setIsSearchAgentsOpen(false)}
        onAgentSaved={(savedAgent) => {
          setSavedAgents(prev => [...prev, savedAgent]);
          setIsSearchAgentsOpen(false);
        }}
      />
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[800px] p-0">
        <DialogHeader className="p-6 pb-0 flex flex-row justify-between items-center">
          <DialogTitle className="text-2xl font-bold tracking-tight">
            AI Tools
          </DialogTitle>
        </DialogHeader>

        <div className="p-6 max-h-[500px] overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {allTools.map((tool, index) => (
              <div
                key={`tool-${index}`}
                onClick={() => handleToolSelect(tool)}
                className="group relative flex flex-col items-center text-left p-6 rounded-lg border border-border bg-card transition-all duration-200 hover:border-primary hover:shadow-lg cursor-pointer h-[220px]"
              >
                <div className="mb-4 p-2 rounded-full bg-primary/10 text-primary transition-colors duration-200 group-hover:bg-primary group-hover:text-primary-foreground">
                  {tool.icon}
                </div>
                <h3 className="text-lg font-semibold mb-2 w-full text-center truncate">{tool.name}</h3>
                <p className="text-sm text-muted-foreground w-full text-center line-clamp-3 overflow-hidden">
                  {tool.description}
                </p>
                <div className="absolute inset-0 rounded-lg ring-2 ring-transparent transition-all duration-200 group-hover:ring-primary/20" />
              </div>
            ))}
            <div
              key={`add-new-tool`}
              onClick={() => setIsSearchAgentsOpen(true)}
              className="group relative flex flex-col justify-center items-center text-left p-6 rounded-lg border text-gray-400 border-gray-400 border-dashed bg-card transition-all duration-200 cursor-pointer h-[220px] hover:border-gray-500 hover:text-gray-500"
            >
              <PlusIcon/>
              <p>Add New Agent</p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OtherAIDialog;